import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { IseiLogo } from "@/components/IseiLogo";
import Footer from "@/components/Footer";

type NavLink = { label: string; href: string; match?: string[] };
type NavGroup = { label: string; id: string; items: NavLink[] };
type NavEntry = NavLink | NavGroup;

const navEntries: NavEntry[] = [
  { label: "Research", href: "/articles", match: ["/articles", "/research"] },
  { label: "Work", href: "/work" },
  {
    label: "Data tools",
    id: "nav-data",
    items: [
      { label: "Dashboard", href: "/dashboard" },
      { label: "RHTP", href: "/rhtp" },
    ],
  },
  { label: "About", href: "/about" },
  {
    label: "More",
    id: "nav-more",
    items: [
      { label: "Field Notes", href: "/field-notes" },
      { label: "Corrections", href: "/corrections" },
      { label: "Speaking", href: "/speaking" },
      { label: "Press", href: "/press-kit" },
      { label: "Media", href: "/media" },
    ],
  },
  { label: "Contact", href: "/connect" },
];

const isGroup = (e: NavEntry): e is NavGroup => "items" in e;

function useIsActive() {
  const { pathname } = useLocation();
  return (link: NavLink) =>
    (link.match ?? [link.href]).some(
      (p) => pathname === p || pathname.startsWith(p + "/"),
    );
}

function DesktopGroup({ group }: { group: NavGroup }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isActive = useIsActive();
  const active = group.items.some(isActive);
  const menuId = `${group.id}-menu`;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const focusItem = (dir: 1 | -1 | "first") => {
    const items = Array.from(
      ref.current?.querySelectorAll<HTMLAnchorElement>(`#${menuId} a`) ?? [],
    );
    if (!items.length) return;
    if (dir === "first") return items[0].focus();
    const i = items.indexOf(document.activeElement as HTMLAnchorElement);
    items[(i + dir + items.length) % items.length].focus();
  };

  return (
    <div
      ref={ref}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          setOpen(false);
          buttonRef.current?.focus();
        } else if (open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
          e.preventDefault();
          focusItem(e.key === "ArrowDown" ? 1 : -1);
        }
      }}
      onBlur={(e) => {
        if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => focusItem("first"));
          }
        }}
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-sm text-sm tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          active ? "text-foreground font-semibold" : "text-foreground/80 hover:text-foreground"
        }`}
      >
        {group.label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <ul
        id={menuId}
        hidden={!open}
        className="absolute right-0 top-full mt-3 min-w-[180px] rounded-md border border-border bg-popover p-1.5 shadow-xl"
      >
        {group.items.map((item) => (
          <li key={item.href}>
            <Link
              to={item.href}
              onClick={() => setOpen(false)}
              aria-current={isActive(item) ? "page" : undefined}
              className={`block rounded-sm px-3 py-2 text-sm transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none ${
                isActive(item) ? "text-accent font-semibold" : "text-foreground/90"
              }`}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PublicLayout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isActive = useIsActive();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const mobileRef = useRef<HTMLElement>(null);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    mobileRef.current?.querySelector<HTMLElement>("a")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const linkClass = (link: NavLink) =>
    `whitespace-nowrap rounded-sm text-sm tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
      isActive(link) ? "text-foreground font-semibold" : "text-foreground/80 hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur-2xl">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 flex items-center justify-between gap-6 h-16">
          <Link
            to="/"
            aria-label="David Isei, home"
            className="font-display text-lg font-bold text-foreground tracking-tight flex items-center gap-2.5 shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <IseiLogo size={22} />
            <span className="whitespace-nowrap">David Isei</span>
          </Link>

          <nav aria-label="Main" className="hidden md:flex items-center gap-7">
            {navEntries.map((entry) =>
              isGroup(entry) ? (
                <DesktopGroup key={entry.id} group={entry} />
              ) : (
                <Link
                  key={entry.href}
                  to={entry.href}
                  aria-current={isActive(entry) ? "page" : undefined}
                  className={linkClass(entry)}
                >
                  {entry.label}
                </Link>
              ),
            )}
          </nav>

          <button
            ref={toggleRef}
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className="md:hidden inline-flex h-11 w-11 items-center justify-center -mr-2 text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        <nav
          id="mobile-nav"
          ref={mobileRef}
          aria-label="Main"
          hidden={!menuOpen}
          className="md:hidden max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-border/40 bg-background"
        >
          <div className="max-w-6xl mx-auto px-5 py-4">
            {navEntries.map((entry) =>
              isGroup(entry) ? (
                <div key={entry.id} className="py-3 border-b border-border/30" role="group" aria-labelledby={`${entry.id}-m`}>
                  <p id={`${entry.id}-m`} className="text-xs uppercase tracking-[0.18em] text-accent font-semibold mb-1">
                    {entry.label}
                  </p>
                  <ul className="grid grid-cols-2 gap-x-4">
                    {entry.items.map((item) => (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          aria-current={isActive(item) ? "page" : undefined}
                          className={`block py-2.5 text-base ${isActive(item) ? "text-accent font-semibold" : "text-foreground/90"}`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <Link
                  key={entry.href}
                  to={entry.href}
                  aria-current={isActive(entry) ? "page" : undefined}
                  className={`block py-3 text-base border-b border-border/30 ${
                    isActive(entry) ? "text-accent font-semibold" : "text-foreground"
                  }`}
                >
                  {entry.label}
                </Link>
              ),
            )}
          </div>
        </nav>
      </header>

      <main className="flex-1 pt-16">{children}</main>

      <Footer />
    </div>
  );
}
